import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { Button } from '@atoms/Button';
import { SearchBar } from '@molecules/SearchBar';
import { useAuthStore } from '@store/authStore';
import { cartItemCount, useCartStore } from '@store/cartStore';
import { ROLE_LABELS } from '@utils/formatters';
import { goToSection } from '@/utils/scrollToSection';
import clsx from 'clsx';

interface HeaderProps {
  mode?: 'marketing' | 'app';
}

type MarketingLink =
  | { label: string; to: '/' }
  | { label: string; sectionId: 'categories' | 'wholesalers' };

const MARKETING_LINKS: MarketingLink[] = [
  { label: 'Ana Sayfa', to: '/' },
  { label: 'Kategoriler', sectionId: 'categories' },
  { label: 'Toptancılar', sectionId: 'wholesalers' },
];

export function Header({ mode = 'app' }: HeaderProps) {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuthStore();
  const { items, toggleDrawer } = useCartStore();
  const count = cartItemCount(items);
  const isMarketing = mode === 'marketing';

  const dashboardPath =
    user?.role === 'admin'
      ? '/admin'
      : user?.role === 'wholesaler'
        ? '/wholesaler'
        : user?.role === 'restaurant'
          ? '/restaurant'
          : '/login';

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 shadow-header backdrop-blur-xl">
      <div className="section-container flex items-center gap-4 py-3">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900 text-xs font-bold tracking-tight text-white">
            LY
          </span>
          <span className="hidden font-bold text-slate-900 sm:block">
            Lezzet Yolda <span className="font-semibold text-slate-500">Toptan</span>
          </span>
        </Link>

        {isMarketing && (
          <nav className="hidden items-center gap-1 lg:flex" aria-label="Ana navigasyon">
            {MARKETING_LINKS.map((link) =>
              'sectionId' in link ? (
                <button
                  key={link.label}
                  type="button"
                  onClick={() => goToSection(navigate, link.sectionId)}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
                >
                  {link.label}
                </button>
              ) : (
                <Link
                  key={link.label}
                  to={link.to}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
                >
                  {link.label}
                </Link>
              ),
            )}
          </nav>
        )}

        <div className={clsx('flex-1', isMarketing ? 'max-w-md lg:ml-auto' : 'hidden md:block md:max-w-xl')}>
          <SearchBar className="w-full" />
        </div>

        <div className="ml-auto flex items-center gap-2">
          {isAuthenticated && user?.role === 'restaurant' && (
            <button
              type="button"
              onClick={toggleDrawer}
              className="relative rounded-xl p-2.5 text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
              aria-label="Sepet"
            >
              <ShoppingCart className="h-5 w-5" />
              {count > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-brand-500 text-[10px] font-bold text-white">
                  {count}
                </span>
              )}
            </button>
          )}

          {isAuthenticated && user?.role === 'restaurant' && (
            <a
              href={import.meta.env.VITE_LY_WEB_URL || 'https://lezzetyolda.com/restaurant-panel'}
              className="hidden rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-900 sm:inline-flex"
            >
              Teslimat Platformu
            </a>
          )}

          {isAuthenticated ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => navigate(dashboardPath)}>
                {ROLE_LABELS[user!.role]} Panel
              </Button>
              <Button variant="outline" size="sm" onClick={() => logout()}>
                Çıkış
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={() => navigate('/login')}>
              Giriş Yap
            </Button>
          )}
        </div>
      </div>

      {!isMarketing && (
        <div className="border-t border-slate-100 px-4 py-2 md:hidden">
          <SearchBar />
        </div>
      )}

      {isMarketing && (
        <div className="border-t border-slate-100 px-4 py-2 lg:hidden">
          <SearchBar />
        </div>
      )}
    </header>
  );
}
